/**
 * @spec docs/specs/features/staking-position-ui.md#design-system
 *
 * Mobile navigation menu with slide-out drawer.
 * Keeps wallet status visible in header, moves nav to drawer.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { routes } from '@/lib/routes';
import { useAuth } from '@/hooks/useAuth';
import { showToast, TOAST_MESSAGES } from '@/lib/toast';
import DashboardIcon from '@/assets/icons/dashboard.svg?react';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  isConnected: boolean;
  isAuthenticated: boolean;
  selectedAddress: string | null;
  email?: string | null;
}

interface MobileNavLinkProps {
  to: string;
  isActive: boolean;
  children: React.ReactNode;
  icon?: React.ReactNode;
  onClick: () => void;
}

function MobileNavLink({ to, isActive, children, icon, onClick }: MobileNavLinkProps) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={cn(
        'flex items-center gap-3 px-4 py-3 rounded-lg',
        'font-bold text-base transition-colors',
        isActive ? 'bg-white/10 text-white' : 'text-white/70 hover:text-white hover:bg-white/5'
      )}
    >
      {icon && <span className="size-5">{icon}</span>}
      <span>{children}</span>
    </Link>
  );
}

export function MobileMenu({
  isOpen,
  onClose,
  isConnected,
  isAuthenticated,
  selectedAddress,
  email,
}: MobileMenuProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);
  const { logout, login } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = useCallback(async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      showToast.success(
        isAuthenticated ? TOAST_MESSAGES.LOGGED_OUT : TOAST_MESSAGES.WALLET_DISCONNECTED
      );
      onClose();
      navigate('/');
    } catch {
      showToast.error(TOAST_MESSAGES.LOGOUT_FAILED);
    } finally {
      setIsLoggingOut(false);
    }
  }, [logout, onClose, navigate, isAuthenticated]);

  // Close menu on route change
  useEffect(() => {
    onClose();
  }, [location.pathname, onClose]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when menu is open
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      // Delay to prevent immediate close from toggle button
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const isHome = location.pathname === routes.home();
  const isAddress = location.pathname.startsWith('/address/');
  const isStake = location.pathname === routes.stake();
  const isSwap = location.pathname === routes.swap();

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 bg-black/60 backdrop-blur-sm z-40',
          'transition-opacity duration-300',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={menuRef}
        className={cn(
          'fixed top-0 right-0 h-full w-[280px] max-w-[80vw]',
          'bg-bg-primary border-l border-white/10',
          'z-50 shadow-2xl',
          'transition-transform duration-300 ease-out',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-5 border-b border-white/10">
          <span className="text-sm font-semibold text-white/50">Menu</span>
          <button
            onClick={onClose}
            className="p-2 -mr-2 text-white/50 hover:text-white transition-colors"
            aria-label="Close menu"
          >
            <svg className="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* User info */}
        {email && (
          <div className="px-4 py-3 border-b border-white/10">
            <span className="text-xs text-text-muted">Signed in as</span>
            <p className="text-sm text-white truncate">{email}</p>
          </div>
        )}

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          <MobileNavLink
            to={routes.home()}
            isActive={isHome}
            icon={<DashboardIcon className="size-5" />}
            onClick={onClose}
          >
            Dashboard
          </MobileNavLink>

          {(isConnected || isAuthenticated) && selectedAddress && (
            <MobileNavLink
              to={routes.address(selectedAddress)}
              isActive={isAddress}
              onClick={onClose}
            >
              Portfolio
            </MobileNavLink>
          )}

          {(isConnected || isAuthenticated) && (
            <MobileNavLink to={routes.stake()} isActive={isStake} onClick={onClose}>
              Stake
            </MobileNavLink>
          )}

          {(isConnected || isAuthenticated) && (
            <MobileNavLink to={routes.swap()} isActive={isSwap} onClick={onClose}>
              Swap
            </MobileNavLink>
          )}

          <a
            href="https://play.phenompoker.com"
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClose}
            className={cn(
              'flex items-center gap-3 px-4 py-3 rounded-lg',
              'font-bold text-base transition-colors',
              'text-white/70 hover:text-white hover:bg-white/5'
            )}
          >
            Play
          </a>
        </nav>

        {/* Log Out or Sign In */}
        {(isAuthenticated || isConnected) && (
          <div className="px-4 mt-auto pb-6 border-t border-white/10 pt-4">
            {!isAuthenticated && isConnected ? (
              <button
                type="button"
                onClick={() => login()}
                className={cn(
                  'flex items-center gap-3 px-4 py-3 rounded-lg w-full',
                  'font-bold text-base transition-colors',
                  'text-accent-purple hover:bg-accent-purple/10'
                )}
              >
                Sign In
              </button>
            ) : (
              <button
                type="button"
                onClick={() => void handleLogout()}
                disabled={isLoggingOut}
                className={cn(
                  'flex items-center gap-3 px-4 py-3 rounded-lg w-full',
                  'font-bold text-base transition-colors',
                  'text-red-400 hover:bg-red-400/10',
                  isLoggingOut && 'opacity-50 cursor-not-allowed'
                )}
              >
                {isLoggingOut ? 'Logging out...' : 'Log Out'}
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
}
